// react
import { useCallback, useEffect, useRef, useState } from 'react';

// insights
import { appInsights, trackEvent } from '../insights';

// props
export interface UseFetchParams {
	isEnabled?: boolean;
	isLazy?: boolean;
	onAbort?: () => void;
	onComplete?: () => void;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	onError?: (error: any) => void;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	onSuccess?: (data: any) => void;
	options?: object;
	url?: string;
}

export const useFetch = ({
	isEnabled = true,
	isLazy = false,
	onAbort,
	onComplete,
	onError,
	onSuccess,
	options = {},
	url,
}: UseFetchParams) => {
	// state
	const [error, setError] = useState<Error | null>(null);
	const [isLoading, setIsLoading] = useState(() => !isLazy && isEnabled && Boolean(url));

	// refs
	const refOnComplete = useRef(onComplete);
	const refOnError = useRef(onError);
	const refOnSuccess = useRef(onSuccess);
	const refOptions = useRef(options);
	const refUrl = useRef(url);
	const refAbortController = useRef<AbortController | null>(null);
	const refIsMounted = useRef(true);

	const fetchRequest = useCallback(
		async (u = refUrl.current, o = refOptions.current) => {
			// Abort any ongoing request
			if (refAbortController.current) {
				refAbortController.current.abort();
			}

			// Create a new AbortController
			refAbortController.current = new AbortController();

			// set loading
			if (refIsMounted.current) {
				setIsLoading(true);
			}

			// vars
			const { headers = {}, ...opts } = o;
			const idOperation = crypto.randomUUID();

			try {
				const req = await fetch(u, {
					...opts,
					headers: {
						...headers,
						'Request-Id': idOperation,
						'X-Operation-Id': idOperation,
					},
					signal: refAbortController.current.signal,
				});
				const res = await req.json();

				if (res.error) {
					throw res.error;
				}

				if (refIsMounted.current && refOnSuccess.current) {
					refOnSuccess.current(res);
				}

				// track dependency
				appInsights.trackDependencyData({
					correlationContext: idOperation,
					id: idOperation,
					name: u,
					responseCode: res.status,
					success: req.ok,
				});
				trackEvent('DataFetchSuccess', { success: true });

				return res;
			} catch (error) {
				if (refAbortController.current.signal.aborted) {
					// Handle aborted request
					console.log('Fetch request was aborted');
					if (refIsMounted.current && onAbort) onAbort();
				} else {
					const err = error as Error;
					if (refIsMounted.current && refOnError.current) {
						refOnError.current(err);
					}
					if (refIsMounted.current) {
						setError(err);
					}
					trackEvent('DataFetchError', { message: error });
				}
			} finally {
				if (refIsMounted.current && refOnComplete.current) {
					refOnComplete.current();
				}
				if (refIsMounted.current) {
					setIsLoading(false);
				}
			}
		},
		[onAbort]
	);

	const refetch = useCallback(() => {
		if (refIsMounted.current) {
			setIsLoading(true);
		}
		fetchRequest();
	}, [fetchRequest]);

	// update url on change
	useEffect(() => {
		refUrl.current = url;
	}, [url]);

	// fetch when isEnabled
	useEffect(() => {
		if (!isLazy && isEnabled && url) {
			setIsLoading(true);
			fetchRequest();
		} else if (!isEnabled) {
			setIsLoading(false);
		}
	}, [fetchRequest, isEnabled, isLazy, url]);

	// update options ref when options change
	useEffect(() => {
		refOptions.current = options;
	}, [options]);

	// update onSuccess ref when onSuccess change
	useEffect(() => {
		refOnSuccess.current = onSuccess;
	}, [onSuccess]);

	// Cleanup on unmount
	useEffect(() => {
		refIsMounted.current = true;
		return () => {
			refIsMounted.current = false;
			if (refAbortController.current) {
				refAbortController.current.abort();
			}
		};
	}, []);

	return {
		error,
		fetchRequest,
		isLoading,
		refetch,
	};
};
